/**
 * React Query mutation for creating chats for matches
 */

import chatService from "@features/messaging/services/chatService";
import logger from "@logger";
import { chatsQueryKeys, matchesQueryKeys } from "@shared/queryKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Data structure for create chat mutation
 */
export interface CreateChatData {
  /** First user ID */
  userId1: string;
  /** Second user ID */
  userId2: string;
}

/**
 * Options for the create chat mutation
 */
export interface UseCreateChatOptions {
  /** Callback when chat creation is successful */
  onSuccess?: (chatId: string, variables: CreateChatData, context: unknown) => void;
  /** Callback when chat creation fails */
  onError?: (error: Error, variables: CreateChatData, context: unknown) => void;
  /** Callback when chat creation completes (success or error) */
  onSettled?: (chatId: string | undefined, error: Error | null, variables: CreateChatData, context: unknown) => void;
}

/**
 * Hook to create a chat for a match
 */
export function useCreateChat(options?: UseCreateChatOptions) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateChatData) => {
      return await chatService.createChatForMatch(data.userId1, data.userId2);
    },
    onSuccess: (chatId, variables, context) => {
      // Invalidate matches queries for both users so lists refetch
      for (const uid of [variables.userId1, variables.userId2]) {
        queryClient.invalidateQueries({ queryKey: matchesQueryKeys.allMatches(uid) });
      }

      // Invalidate chats queries
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.user(variables.userId1),
      });
      queryClient.invalidateQueries({
        queryKey: chatsQueryKeys.user(variables.userId2),
      });

      logger.info("Chat created successfully", { chatId, variables });

      options?.onSuccess?.(chatId, variables, context);
    },
    onError: (error, variables, context) => {
      logger.error("Error creating chat:", error);
      options?.onError?.(error, variables, context);
    },
    onSettled: (chatId, error, variables, context) => {
      options?.onSettled?.(chatId, error, variables, context);
    },
  });
}
